import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { UserRole } from '../../../swagger/api';
import { appInsights } from '../../../utils/appInsights';
import activateMitIDLogin from '../../../utils/mitidActivation';
import RouteUrlPaths from '../../../RouteUrlPaths';
import AuthenticationStore from '../../../stores/AuthenticationStore';
import UserStore from '../../../stores/UserStore';
import ManageUserAccessStore from '../../../stores/ManageUserAccessStore';
import NotificationStore from '../../../stores/NotificationStore';

interface UseAuthenticationDataLoaderProps {
  authenticationStore: AuthenticationStore;
  userStore: UserStore;
  manageUserAccessStore: ManageUserAccessStore;
  notificationStore: NotificationStore;
}

const useAuthenticationDataLoader = ({
  authenticationStore,
  userStore,
  manageUserAccessStore,
  notificationStore
}: UseAuthenticationDataLoaderProps) => {
  const navigate = useNavigate();

  const pushMatomoPageview = useCallback((customerType?: string) => {
    // eslint-disable-next-line no-underscore-dangle
    const mtm = (window as any)._mtm;
    if (mtm) {
      if (customerType) {
        mtm.push({ customerType });
      } else {
        mtm.push({ customerType: '' });
      }
      mtm.push({ event: 'customerDataReadyForMatomo' });
    }
  }, []);

  const loadData = useCallback(async () => {
    await userStore.loadCurrentUser();

    let auth0User = null;
    if (authenticationStore.authContext) {
      try {
        auth0User = await authenticationStore.authContext.getUser();
      } catch (error) {
        appInsights.trackTrace({
          message: '[AuthDataLoader] No Auth0 user data available (likely React Native mode)'
        });
      }
    }

    const mitIdLoginDisabledClaim = auth0User?.['https://brobizz.com/isMitIdLoginDisabled'];
    if (mitIdLoginDisabledClaim === true) {
      authenticationStore.setIsMitIdLoginDisabled(true);
    }

    const auth0MitIdEnrolled = !!auth0User?.app_metadata?.isMitIdEnrolled;
    const signUpInProgress = userStore.person?.signUpInProgress;
    const accountId = userStore.currentAccount?.id || userStore.person?.accountIds?.[0];

    if (
      typeof auth0MitIdEnrolled === 'boolean' &&
      signUpInProgress === false &&
      accountId &&
      userStore.person &&
      userStore.person.isMitIdEnrolled !== auth0MitIdEnrolled
    ) {
      try {
        await userStore.updateMitIdStatus(auth0MitIdEnrolled);
      } catch (error) {
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error(String(error)),
          severityLevel: SeverityLevel.Error
        });
      }
    }

    if (manageUserAccessStore.isPrivateCustomer()) {
      notificationStore.getNotifications();
    }

    if (userStore.person?.signUpInProgress === true) {
      navigate(RouteUrlPaths.CreateCustomer);
    }

    if (userStore.isSelectAccountShown) {
      navigate(RouteUrlPaths.SelectAccount);
    }

    if (manageUserAccessStore.shouldForceConfirmPersonalDetails()) {
      navigate(RouteUrlPaths.ConfirmPersonalDetails);
    }

    if (userStore.person?.role) {
      const customerTypeValue =
        userStore.person.role === UserRole.PrivatePerson ? 'Private' : 'Commercial';
      pushMatomoPageview(customerTypeValue);
    } else {
      pushMatomoPageview();
    }
  }, [
    authenticationStore,
    userStore,
    manageUserAccessStore,
    notificationStore,
    navigate,
    pushMatomoPageview
  ]);

  const handleMitIdRedirect = useCallback(
    async (searchParams: URLSearchParams) => {
      if (searchParams.get('redirectToMitId') && authenticationStore.authContext) {
        try {
          await activateMitIDLogin(authenticationStore.authContext, userStore);
        } catch (error) {
          appInsights.trackTrace({
            message: '[AuthDataLoader] MitID redirect failed'
          });
        }
      }
    },
    [authenticationStore.authContext, userStore]
  );

  return {
    loadData,
    handleMitIdRedirect
  };
};

export default useAuthenticationDataLoader;
